import {_} from 'lodash';
import {ICompanySyncstep, ISyncCycleStep, ISyncstep, ISyncStepStatus, TMessageType} from "../index";
import {SyncstepId} from "../constants";
import {isPartOf} from "./misc";
import {lm} from "../appContext";

export namespace nsSync {

    export enum SyncStatus {
        Unknown,
        Executing,
        Executed,
        Halted,
    }

    interface ISyncObject {
        lastsyncstep: ISyncCycleStep
        syncsteps: ICompanySyncstep[]
    }

    export interface ISyncStatusDefinition {
        status: SyncStatus
        message: string
        icon: string
        clsText: string
        clsFull: TMessageType
    }

    export const definitions: ISyncStatusDefinition[] = [];
    definitions[SyncStatus.Unknown] = {
        status: SyncStatus.Unknown,
        message: 'unknown',
        icon: 'fa-exclamation-triangle',
        clsText: 'text-warning',
        clsFull: 'warning',
    };
    definitions[SyncStatus.Executing] = {
        status: SyncStatus.Executing,
        message: 'ongoing|td',
        icon: 'fa-hourglass-half',
        clsText: 'text-info',
        clsFull: 'info',
    };
    definitions[SyncStatus.Executed] = {
        status: SyncStatus.Executed,
        message: 'completed',
        icon: 'fa-check-circle',
        clsText: 'text-success',
        clsFull: 'success',
    };
    definitions[SyncStatus.Halted] = {
        status: SyncStatus.Halted,
        message: 'stopped',
        icon: 'fa-times-circle',
        clsText: 'text-danger',
        clsFull: 'danger',
    };

    export function getStatus(syncObj: ISyncObject): SyncStatus {
        if (!syncObj.lastsyncstep) {
            return SyncStatus.Unknown;
        }
        if (syncObj.lastsyncstep.halted) {
            return SyncStatus.Halted;
        }
        if (syncObj.lastsyncstep.ended && isLastStep(syncObj)) {
            return SyncStatus.Executed;
        } else {
            return SyncStatus.Executing;
        }
    }

    export function getStatusDefinition(syncObj: ISyncObject): ISyncStatusDefinition {
        return definitions[getStatus(syncObj)]!;
    }

    export function isLastStep(syncObj: ISyncObject): boolean {
        const sorted = _.sortBy(syncObj.syncsteps, (s: ICompanySyncstep) => s.order);
        const last = sorted[sorted.length - 1];
        return last.syncstep.name == syncObj.lastsyncstep.syncstepname;
    }

    export function getStatusDefinitionFromSyncStatus(item: ISyncStepStatus): ISyncStatusDefinition {
        return definitions[getSyncStatus(item)]!;
    }

    export function getSyncStatus(item: ISyncStepStatus): SyncStatus {
        if (!!item.halted) {
            return SyncStatus.Halted;
        }

        if (!!item.ended && item.islaststep) {
            return SyncStatus.Executed;
        }

        if (!!item.started && (item.islaststep ? !item.ended : true)) {
            return SyncStatus.Executing;
        }

        return SyncStatus.Unknown;
    }

    export function isSyncstepActiveDirectory(syncstepId: ISyncstep['id']): boolean {
        return isSyncstepOnPremAD(syncstepId) || isSyncstepId(syncstepId, SyncstepId.Export_Azure)
    }

    /** On-prem AD exports 1–9. Not Azure — see isSyncstepActiveDirectory. */
    export function isSyncstepOnPremAD(syncstepId?: ISyncstep['id'] | null): boolean {
        if (syncstepId == null) {
            return false;
        }
        return isSyncstepId(
            syncstepId,
            SyncstepId.Export_AD,
            SyncstepId.Export_AD2,
            SyncstepId.Ad3,
            SyncstepId.Export_AD4,
            SyncstepId.Export_AD5,
            SyncstepId.Export_AD6,
            SyncstepId.Export_AD7,
            SyncstepId.Export_AD8,
            SyncstepId.Export_AD9,
        )
    }

    export function isSyncstepId(syncstepId: ISyncstep['id'], ...syncstepIds: SyncstepId[]): boolean {
        if (!syncstepId) {
            return false
        }
        return isPartOf<ISyncstep['id']>(syncstepId, ...syncstepIds)
    }

    export function getSyncstepName(syncstep: ISyncstep, translate: boolean = true): string {
        const key = 'syncstep.name.' + syncstep.id;
        if (lm.hasTranslation(key)) {
            return translate ? (lm.t(key) ?? syncstep.name) : key
        }
        return syncstep.name
    }

}
